import type { MatrixPos, Task } from "@app/core";
import { addDays, endOfDay, fromIso, matrixCellCenter, matrixIndexOf, matrixRowForPriority, matrixScore, startOfDay } from "@app/core";

import { isOverdue, taskSlotEnd } from "../agenda/agenda.js";

/** Pure model for the Priorities (matrix decision) view: which tasks are in the
 *  chosen time range, where each sits on the importance×urgency board, and the
 *  suggested execution order. Callers apply `useViewFilters` + collapse
 *  recurring series to their next occurrence (`tasksForList`) BEFORE building. */

export type Horizon = "today" | "d3" | "d7" | "d14" | "d30" | "all";

export const HORIZONS: Horizon[] = ["today", "d3", "d7", "d14", "d30", "all"];

const HORIZON_DAYS: Record<Horizon, number | null> = {
  today: 1, d3: 3, d7: 7, d14: 14, d30: 30, all: null,
};

/** Inclusive end of a horizon (end of its last local day); null = unbounded. */
export function horizonEnd(horizon: Horizon, now: Date): Date | null {
  const days = HORIZON_DAYS[horizon];
  return days === null ? null : endOfDay(addDays(startOfDay(now), days - 1));
}

/** Queue band = the diagonal third of the item's effective position — the same
 *  banding as the derived discrete priority, so band and flag never disagree. */
export type QueueBand = "now" | "next" | "later" | "unranked";

const BAND_BY_INDEX: QueueBand[] = ["later", "next", "now"];

export interface ViewItem {
  task: Task;
  /** Effective display position (deadline-derived urgency folded in);
   *  null when the item has neither a placement nor a flag (it sits in the
   *  triage tray) or the matrix scope doesn't cover it. */
  pos: MatrixPos | null;
  /** Whether the task is actually matrix-placed (`matrix_pos != null`);
   *  false + non-null `pos` = a flag-only ghost. */
  placed: boolean;
  /** Diagonal score of `pos` (0..1); null when `pos` is null. */
  score: number | null;
  band: QueueBand;
  overdue: boolean;
  done: boolean;
  /** Open "later"-band item untouched (no edit of any kind) for
   *  {@link STALE_AFTER_DAYS}+ — surfaced so it prompts an explicit
   *  keep / snooze / delete decision instead of rotting silently. */
  stale: boolean;
}

/** Days without any edit after which an open "later" item reads as stale. */
export const STALE_AFTER_DAYS = 14;

/** Whole weeks since the task was last touched (its `updated_at`). */
export function staleWeeks(task: Task, now: Date): number {
  if (!task.updated_at) return 0;
  return Math.max(0, Math.floor((now.getTime() - fromIso(task.updated_at).getTime()) / (7 * 86_400_000)));
}

/** Per-task memo of the built {@link ViewItem}s. A `ViewItem` is a pure function
 *  of (task, `displayOf`, `covered`, `now`), so an edit to ONE task can reuse
 *  every other item's object — which is what lets the board/queue rows memoize
 *  and skip re-rendering. Hold one in a `useRef` and pass it to
 *  {@link buildMatrixView}. */
export interface MatrixItemCache {
  deps: { displayOf: unknown; covered: unknown; nowMs: number } | null;
  items: Map<string, ViewItem>;
}

export function createMatrixItemCache(): MatrixItemCache {
  return { deps: null, items: new Map() };
}

export interface MatrixViewOptions {
  horizon: Horizon;
  includeOverdue: boolean;
  includeUndated: boolean;
  includeDone: boolean;
  /** Narrow to one local day (picked on the load strip); overrides the horizon
   *  bounds so a strip day beyond the horizon still shows its tasks. */
  dayFocus: Date | null;
  /** Whether the `matrix_hidden` flag applies (default true). Pass false when
   *  the matrix scope is "off": hiding is part of the matrix system — its only
   *  toggles live in the matrix widget and on board chips, none of which exist
   *  off-scope — so honouring the flag there would drop entries from the queue
   *  with no control left to bring them back. The flag stays stored and takes
   *  effect again the moment the matrix is re-enabled. */
  hidingActive?: boolean;
}

export interface MatrixViewModel {
  /** Items with a position — rendered on the board (placed dots + flag ghosts). */
  board: ViewItem[];
  /** Position-less items — the triage tray, sorted like the queue tail. */
  tray: ViewItem[];
  /** Every item in decision order (board + tray). */
  queue: ViewItem[];
  /** Open (not done) items in the range. */
  openCount: number;
  /** Summed `duration_minutes` of the open items. */
  totalMinutes: number;
}

/**
 * Decision order: highest diagonal score first (position-less items last), then
 * nearer deadline (`deadline_at ?? due_at`, none last), then shorter duration
 * (quick wins break ties), then title. Done items sink to the bottom.
 */
export function compareDecision(a: ViewItem, b: ViewItem): number {
  if (a.done !== b.done) return a.done ? 1 : -1;
  const sa = a.score ?? -1;
  const sb = b.score ?? -1;
  if (sa !== sb) return sb - sa;
  const da = a.task.deadline_at ?? a.task.due_at;
  const db = b.task.deadline_at ?? b.task.due_at;
  if (da !== db) {
    if (!da) return 1;
    if (!db) return -1;
    // Imported/synced ISO values may spell the same timeline with `Z` or an
    // explicit offset. Text ordering compares wall-clock spellings, not the
    // instants the user is actually deciding between.
    const at = fromIso(da).getTime();
    const bt = fromIso(db).getTime();
    const aValid = Number.isFinite(at);
    const bValid = Number.isFinite(bt);
    if (aValid !== bValid) return aValid ? -1 : 1;
    if (aValid && at !== bt) return at - bt;
  }
  const dur = a.task.duration_minutes - b.task.duration_minutes;
  if (dur !== 0) return dur;
  return a.task.title.localeCompare(b.task.title);
}

/**
 * Builds the board/tray/queue for the view. `displayOf` resolves a task's
 * effective matrix display position (bind `useEffectiveMatrixDisplay`);
 * `covered` is the matrix-scope gate (`matrixEnabledFor`) — both injected to
 * keep this module pure. Range rules: dated items due from today through the
 * horizon end; overdue open tasks and undated tasks are opt-in extras; done
 * items only with `includeDone` (and only from today onward); snoozed items
 * never (they are deliberately parked — the Today view owns waking them).
 *
 * Banding follows the same placed-gate as `effectivePriorityOf`: only a
 * covered AND matrix-placed task bands by its (deadline-derived) diagonal;
 * everything else bands by its discrete flag — so the queue can never disagree
 * with the sidebar priority filter. Flag-only ghosts still get their display
 * position for the board, exactly like the form widget's ghost dot.
 */
export function buildMatrixView(
  tasks: ReadonlyArray<Task>,
  opts: MatrixViewOptions,
  now: Date,
  displayOf: (task: Task) => MatrixPos | null,
  covered: (task: Task) => boolean,
  /** Optional identity memo — see {@link MatrixItemCache}. */
  cache?: MatrixItemCache,
): MatrixViewModel {
  const from = startOfDay(now);
  const end = opts.dayFocus ? null : horizonEnd(opts.horizon, now);

  const candidates = tasks.filter((t) => {
    if (t.deleted_at || t.status === "snoozed") return false;
    // Explicitly hidden from the decision surfaces (standing commitments like
    // "sleep" / "work" that occupy time but are not decisions). They still count
    // for the load strip and planning availability — those read the task list
    // directly, not this model. Ignored entirely while the matrix is off (see
    // `hidingActive`).
    if (t.matrix_hidden && opts.hidingActive !== false) return false;
    if (t.status === "done" && !opts.includeDone) return false;
    return true;
  });

  let kept: Task[];
  if (opts.dayFocus) {
    // Match the load strip exactly: a timed task spanning midnight occurs on
    // every day its real interval overlaps, not only on its start day.
    kept = candidates.filter((task) => allocationOnDay(task, opts.dayFocus!).occurs);
  } else {
    kept = candidates.filter((t) => {
      if (!t.due_at) return opts.includeUndated;
      const due = fromIso(t.due_at);
      if (end && due.getTime() > end.getTime()) return false;
      if (t.status === "done") return due.getTime() >= from.getTime();
      if (isOverdue(t, now)) return opts.includeOverdue;
      // Past but not overdue (elapsed events, finished multi-day edge): only
      // keep while the slot is still running.
      return taskSlotEnd(t)!.getTime() >= now.getTime();
    });
  }

  const staleCutoff = now.getTime() - STALE_AFTER_DAYS * 86_400_000;
  if (cache) {
    const deps = { displayOf, covered, nowMs: now.getTime() };
    const prev = cache.deps;
    if (!prev || prev.displayOf !== displayOf || prev.covered !== covered || prev.nowMs !== deps.nowMs) {
      cache.items = new Map();
      cache.deps = deps;
    }
  }
  const reused = cache?.items;
  const fresh = reused ? new Map<string, ViewItem>() : null;
  const items: ViewItem[] = kept.map((t) => {
    const hit = reused?.get(t.id);
    if (hit && hit.task === t) {
      fresh?.set(t.id, hit);
      return hit;
    }
    const isCovered = covered(t);
    const pos = isCovered ? displayOf(t) : null;
    const placed = isCovered && t.matrix_pos != null;
    let score: number | null;
    if (placed && pos) {
      score = matrixScore(pos);
    } else {
      // Placed-gate fallback: the discrete flag governs; its score is the
      // flag row's diagonal center so flagged items sort within their band.
      const row = matrixRowForPriority(t.priority);
      score = row === null ? null : matrixCellCenter(row);
    }
    const band: QueueBand = score === null ? "unranked" : BAND_BY_INDEX[matrixIndexOf(score)]!;
    const item: ViewItem = {
      task: t,
      pos,
      placed,
      score,
      band,
      overdue: t.status === "open" && isOverdue(t, now),
      done: t.status === "done",
      stale: band === "later" && t.status === "open"
        && !!t.updated_at && fromIso(t.updated_at).getTime() < staleCutoff,
    };
    fresh?.set(t.id, item);
    return item;
  });
  if (cache && fresh) cache.items = fresh; // drop items whose task left the view

  const queue = [...items].sort(compareDecision);
  const open = items.filter((i) => !i.done);
  return {
    board: queue.filter((i) => i.pos !== null),
    tray: queue.filter((i) => i.pos === null),
    queue,
    openCount: open.length,
    totalMinutes: open.reduce((sum, i) => sum + i.task.duration_minutes, 0),
  };
}

/**
 * The entries {@link buildMatrixView} drops for `matrix_hidden`, for the
 * collapsible "hidden" list at the bottom of the Priorities page. Deliberately
 * NOT horizon- or status-filtered: the list exists to review and undo hiding,
 * so a hidden series whose next occurrence is months out must still be
 * reachable. Callers pass the same filtered + series-collapsed list they hand
 * to `buildMatrixView`.
 */
export function hiddenMatrixTasks(tasks: ReadonlyArray<Task>): Task[] {
  return tasks
    .filter((task) => task.matrix_hidden && !task.deleted_at)
    .sort((a, b) => a.title.localeCompare(b.title));
}

export interface DayLoad {
  date: Date;
  /** Open items occurring on the day (recurrence-EXPANDED — pass the windowed
   *  expansion, not the collapsed list, so a daily series weighs every day). */
  tasks: Task[];
  minutes: number;
}

interface DayAllocation {
  occurs: boolean;
  minutes: number;
}

/** Calendar-day ordinal that is stable across local DST transitions. */
function localDayOrdinal(date: Date): number {
  return Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()) / 86_400_000;
}

/** Portion of a task's slot assigned to one local day. All-day durations are
 *  logical 1440-minute day units; timed durations are real interval overlap. */
function allocationOnDay(task: Task, date: Date): DayAllocation {
  if (!task.due_at) return { occurs: false, minutes: 0 };
  const due = fromIso(task.due_at);
  if (task.all_day) {
    const offset = localDayOrdinal(date) - localDayOrdinal(due);
    const duration = Math.max(0, task.duration_minutes);
    const spanDays = Math.max(1, Math.ceil(duration / 1440));
    if (offset < 0 || offset >= spanDays) return { occurs: false, minutes: 0 };
    return { occurs: true, minutes: Math.min(1440, Math.max(0, duration - offset * 1440)) };
  }

  const dayStart = startOfDay(date).getTime();
  const dayEnd = startOfDay(addDays(date, 1)).getTime();
  const start = due.getTime();
  const duration = Math.max(0, task.duration_minutes);
  const end = start + duration * 60_000;
  if (duration === 0) {
    return { occurs: start >= dayStart && start < dayEnd, minutes: 0 };
  }
  const overlap = Math.max(0, Math.min(end, dayEnd) - Math.max(start, dayStart));
  return { occurs: overlap > 0, minutes: Math.round(overlap / 60_000) };
}

/** Per-day load for the upcoming strip: `days` consecutive days from today. */
export function buildDayLoads(expanded: ReadonlyArray<Task>, now: Date, days: number): DayLoad[] {
  const start = startOfDay(now);
  return Array.from({ length: days }, (_, i) => {
    const date = addDays(start, i);
    const allocated = expanded
      .filter((task) => task.status === "open")
      .map((task) => ({ task, ...allocationOnDay(task, date) }))
      .filter((item) => item.occurs);
    return {
      date,
      tasks: allocated.map((item) => item.task),
      minutes: allocated.reduce((sum, item) => sum + item.minutes, 0),
    };
  });
}

/** Local-day key for strip selection state ("2026-07-16"). */
export function dayKeyOf(date: Date): string {
  const m = `${date.getMonth() + 1}`.padStart(2, "0");
  const d = `${date.getDate()}`.padStart(2, "0");
  return `${date.getFullYear()}-${m}-${d}`;
}
